import { CANCEL_BUTTON,ICON_BUTTON,SUBMIT_BUTTON } from '@redirector/front/lib/formStyles';
import { Loader2,X } from 'lucide-react';
import {
  Children,
  createContext,
  type FormEvent,
  isValidElement,
  type ReactNode,
  useContext,
  useEffect,
  useId,
} from 'react';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ModalProps {
  title: string;
  icon?: ReactNode;
  onClose: () => void;
  loading: boolean;
  noOverlay?: boolean;
  zIndex?: 'z-50' | 'z-[60]';
  onSubmit?: (e: FormEvent) => void;
  showFooter?: boolean;
  titleId?: string;
  headerActions?: ReactNode;
  children: ReactNode;
}

interface ModalContextValue {
  loading: boolean;
  onClose: () => void;
}

/* ------------------------------------------------------------------ */
/*  Context for sub-components                                         */
/* ------------------------------------------------------------------ */

const ModalContext = createContext<ModalContextValue | null>(null);

function useModal(): ModalContextValue {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('Modal buttons must be rendered inside a Modal');
  return ctx;
}

/* ------------------------------------------------------------------ */
/*  Button sub-components                                              */
/* ------------------------------------------------------------------ */

function CloseButton({ className }: { className?: string }) {
  const { loading, onClose } = useModal();
  return (
    <button
      type="button"
      onClick={onClose}
      disabled={loading}
      aria-label="Close modal"
      className={`${className ?? ''} ${ICON_BUTTON} rounded-full`}
    >
      <X className="w-5 h-5" />
    </button>
  );
}

function CancelButton({
  children = 'Cancel',
  disabled = false,
}: {
  children?: ReactNode;
  disabled?: boolean;
}) {
  const { loading, onClose } = useModal();
  return (
    <button
      type="button"
      onClick={onClose}
      disabled={disabled || loading}
      className={CANCEL_BUTTON}
    >
      {children}
    </button>
  );
}

function SubmitButton({
  children,
  disabled,
}: {
  children: ReactNode;
  disabled: boolean;
}) {
  const { loading } = useModal();
  return (
    <button
      type="submit"
      disabled={disabled || loading}
      className={SUBMIT_BUTTON}
    >
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Footer slot                                                        */
/* ------------------------------------------------------------------ */

function Footer({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`shrink-0 px-6 py-4 border-t border-[var(--color-border)] bg-[var(--color-surface-hover)] flex flex-wrap justify-end gap-3 ${className ?? ''}`}
    >
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Modal                                                              */
/* ------------------------------------------------------------------ */

let openModalCount = 0;

export default function Modal({
  title,
  icon,
  onClose,
  loading,
  noOverlay = false,
  zIndex = 'z-50',
  onSubmit,
  showFooter = true,
  titleId: propsTitleId,
  headerActions,
  children,
}: ModalProps) {
  const generatedId = useId();
  const titleId = propsTitleId ?? generatedId;
  const z = zIndex;

  useEffect(() => {
    if (openModalCount === 0) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    openModalCount++;
    return () => {
      openModalCount--;
      if (openModalCount === 0) {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      }
    };
  }, []);

  const ctx: ModalContextValue = { loading, onClose };

  // Separate Modal.Footer from the body children.
  let footerContent: ReactNode = null;
  const bodyContent: ReactNode[] = [];
  for (const child of Children.toArray(children)) {
    if (isValidElement(child) && child.type === Footer) footerContent = child;
    else bodyContent.push(child);
  }

  const body = (
      <div className="flex-1 overflow-y-auto p-6 space-y-5">
      {bodyContent}
    </div>
  );

  const inner = (
    <>
      <div className="shrink-0 px-6 py-4 border-b border-[var(--color-border)] flex items-center justify-between gap-4">
        <h2 id={titleId} className="text-xl text-white font-bold flex items-center gap-2">
          {icon}
          {title}
        </h2>
        <div className="flex items-center gap-1">
          {headerActions}
          <CloseButton />
        </div>
      </div>

      {onSubmit ? (
        <form
          onSubmit={onSubmit}
          className="flex flex-col flex-1 min-h-0 overflow-hidden"
        >
          {body}
          {showFooter && footerContent !== null && footerContent}
        </form>
      ) : (
        <>
          {body}
          {showFooter && footerContent !== null && footerContent}
        </>
      )}
    </>
  );

  return (
    <ModalContext.Provider value={ctx}>
      <div className={`fixed inset-0 ${z} flex items-center justify-center p-4 sm:p-6`}>
        {!noOverlay && (
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" />
        )}

        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className="relative bg-[var(--color-surface)] border border-[var(--color-border)] rounded-2xl max-w-4xl w-full shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
        >
          {inner}
        </div>
      </div>
    </ModalContext.Provider>
  );
}

Modal.CloseButton = CloseButton;
Modal.CancelButton = CancelButton;
Modal.SubmitButton = SubmitButton;
Modal.Footer = Footer;
